import { HttpError } from '@plugins/http'
import ErrorStackParser from 'error-stack-parser'
import debounce from 'lodash.debounce'

const url = `${PUBLIC_PATH}errors/js`
const consoleMinLevel = process.env.NODE_ENV === 'production' ? 300 : 0
const serverMinLevel = 300

declare var console: any

const logLevels: { [key: string]: number } = {
  DEBUG: 100,
  INFO: 200,
  NOTICE: 250,
  WARNING: 300,
  ERROR: 400,
  CRITICAL: 500,
  ALERT: 550,
  EMERGENCY: 600,
}

export interface LoggerInterface {
  logs: any[]
  addErrorRecord: (err: Error) => void
}

interface StackElement {
  column: number
  line: number
  file: string
}

interface Context {
  native?: boolean
  stack?: StackElement[]
  ua?: string
  payload?: any
  url?: string
}

export default class Logger implements LoggerInterface {
  [key: string]: Function | any[]

  logs: any[] = []

  constructor() {
    for (const name in logLevels) {
      if (logLevels.hasOwnProperty(name)) {
        this[name.toLowerCase()] = function() {
          const apply = this.addLogRecord.apply as any
          apply(this, [name, ...arguments])
        }
      }
    }

    this.attachEventListeners()
  }

  attachEventListeners() {
    window.addEventListener('beforeunload', () => {
      if (this.logs.length && navigator.sendBeacon) {
        return this.sendBeacon()
      }
    })

    window.addEventListener('error', (event: ErrorEvent) => {
      this.addErrorRecord(event.error)
    })

    // 'capture' listener, listen to missing images etc
    window.addEventListener(
      'error',
      (event: ErrorEvent) => {
        if (event.target === window) {
          // these are handled by the previous listener
          return
        }

        const target: EventTarget = event.target

        if (target instanceof Element) {
          const tagName: string = target.tagName

          if (tagName === 'IMG') {
            // missing image
            return this.addLogRecord('WARNING', `Missing image: ${(target as HTMLImageElement).src}`)
          }

          if (tagName === 'image') {
            if (!target.getAttribute('xlink:href').length) {
              return
            }
          }

          return this.addLogRecord('WARNING', `Missing asset in ${tagName}`, {
            payload: {
              html: target.outerHTML,
            },
          })
        }
      },
      true,
    )

    window.addEventListener('unhandledrejection', (error) => {
      // unhandled promise rejection
    })

    window.addEventListener('messageerror', (error) => {
      // failed to deserialize worker message
    })
  }

  getLevelFromName(name: string) {
    return logLevels[name.toUpperCase()]
  }

  addErrorRecord(error: Error) {
    let elements

    try {
      elements = ErrorStackParser.parse(error)
    } catch (e) {
      console.error('Could not parse', error)
      return
    }

    const stack: StackElement[] = elements.map((item) => {
      return {
        column: item.columnNumber,
        line: item.lineNumber,
        file: item.fileName,
      }
    })

    const context: Context = {
      native: true,
      url: location.href,
      stack,
    }

    const httpError = error as HttpError

    if (httpError.payload) {
      context.payload = httpError.payload
      context.url = httpError.url
    }

    this.addLogRecord('ERROR', `${error.name} ${error.message}`, context)
  }

  addLogRecord(levelName: string, message: string, context: Context = {}) {
    if (typeof message === 'undefined' || !message.length) {
      return
    }

    // add UA to context
    context.ua = window.navigator.userAgent

    const log = {
      level: levelName,
      message,
      context,
    }

    this.logToConsole(log)

    if (this.getLevelFromName(log.level) < serverMinLevel) {
      return
    }

    this.logs.push(log)
    this.sendToServer()
  }

  /* tslint:disable */
  logToConsole(log: any) {
    if (this.getLevelFromName(log.level) < consoleMinLevel) {
      return
    }

    let methodName = log.level.toLowerCase()
    if (methodName === 'debug') {
      methodName = 'log'
    }

    if (typeof console === 'undefined') {
      return false
    } else if (console[methodName] !== undefined) {
      return console[methodName](log.message)
    } else if (console.log !== undefined) {
      return console.log(log.message)
    }
  }

  collectData() {
    return { logs: this.logs }
  }

  sendToServer = debounce(() => {
    if (navigator.sendBeacon) {
      return this.sendBeacon()
    }

    const xmlhttp = new XMLHttpRequest()
    xmlhttp.open('POST', url)
    xmlhttp.setRequestHeader('Content-Type', 'application/json')
    xmlhttp.send(JSON.stringify(this.collectData()))

    this.logs = []
  }, 5000)
  /* tslint:enable */

  sendBeacon() {
    const fd = new FormData()
    fd.append('logs', JSON.stringify(this.logs))
    fd.append('beacon', '1')

    navigator.sendBeacon(url, fd)

    this.logs = []
  }
}
